import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { WhatsAppIcon } from "@/components/WhatsAppIcon";
import { loadWaSettings } from "@/components/WhatsAppButton";
import { DEFAULT_WA_TEMPLATES, fillTemplate, tplType, waDisplay, waLink, waLinkWithText } from "@/lib/whatsapp";
import { SkipForward, ArrowRight, CheckCircle2 } from "lucide-react";

// Step-through sender: opens each selected client's WhatsApp chat with a personalised message, one at a time.
export function BulkWhatsAppDialog({ clients, onClose }) {
  const { user } = useAuth();
  const [templates, setTemplates] = useState(DEFAULT_WA_TEMPLATES);
  const [tplIdx, setTplIdx] = useState(0);
  const [text, setText] = useState(DEFAULT_WA_TEMPLATES[0].body);
  const [step, setStep] = useState("setup");
  const [pos, setPos] = useState(0);
  const [status, setStatus] = useState({});
  useEffect(() => { loadWaSettings().then((x) => { setTemplates(x.templates); setText(x.templates[0]?.body || ""); }); }, []);

  const tpl = templates[tplIdx];
  const promo = tplType(tpl) === "promotion";
  const excluded = (c) => (!waLink(c.phone) ? "No phone" : promo && !c.promo_consent ? "No promo consent" : null);
  const queue = useMemo(() => clients.filter((c) => !excluded(c)), [clients, promo]); // eslint-disable-line
  const left = clients.filter((c) => excluded(c));
  const cur = queue[pos];
  const msgFor = (c) => fillTemplate(text, { name: c.full_name, first_name: (c.full_name || "").split(" ")[0], file_number: c.file_number || "", service: "", staff_name: user?.name || "" });
  const sent = Object.values(status).filter((v) => v === "sent").length;
  const skipped = Object.values(status).filter((v) => v === "skipped").length;

  const openChat = () => {
    window.open(waLinkWithText(cur.phone, msgFor(cur)), "_blank", "noopener,noreferrer");
    if (status[cur.id] !== "sent") api.post(`/clients/${cur.id}/interactions`, { channel: "WhatsApp", note: `Message — bulk "${tpl?.name || "custom"}"` }).catch(() => {});
    setStatus((s) => ({ ...s, [cur.id]: "sent" }));
  };
  const next = (skip = false) => {
    if (skip && !status[cur.id]) setStatus((s) => ({ ...s, [cur.id]: "skipped" }));
    if (pos + 1 >= queue.length) setStep("done"); else setPos(pos + 1);
  };
  const finish = () => { toast.success(`Bulk WhatsApp: ${sent} opened, ${skipped} skipped`); onClose(); };

  return (
    <Dialog open onOpenChange={(o) => !o && (step === "setup" ? onClose() : finish())}>
      <DialogContent className="sm:max-w-lg" data-testid="bulk-whatsapp-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><WhatsAppIcon className="h-5 w-5 text-[#25D366]" /> WhatsApp {clients.length} client{clients.length === 1 ? "" : "s"}</DialogTitle></DialogHeader>
        {step === "setup" && (
          <div className="space-y-3">
            <div className="flex flex-wrap gap-1.5" data-testid="bulk-wa-templates">
              {templates.map((t, i) => (
                <button key={t.name + i} type="button" data-testid={`bulk-wa-template-${i}`} onClick={() => { setTplIdx(i); setText(t.body); setPos(0); }}
                  className={`px-3 py-1 rounded-full border text-xs font-semibold ${tplIdx === i ? "bg-[#25D366] text-white border-[#25D366]" : "border-slate-200 text-slate-600"}`}>
                  {t.name} <span className={`ml-1 text-[10px] uppercase ${tplIdx === i ? "text-white/80" : tplType(t) === "promotion" ? "text-violet-600" : "text-slate-400"}`}>{tplType(t)}</span>
                </button>
              ))}
            </div>
            <textarea data-testid="bulk-wa-message" value={text} onChange={(e) => setText(e.target.value)} rows={5} className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-[#25D366]" />
            <p className="text-[11px] text-slate-400">Placeholders like {"{first_name}"} are filled in for each client.</p>
            <div className="rounded-xl bg-slate-50 p-3 text-sm" data-testid="bulk-wa-summary">
              <b>{queue.length}</b> will be messaged{left.length > 0 && <>, <b>{left.length}</b> left out:</>}
              {left.length > 0 && <ul className="mt-1 max-h-24 overflow-y-auto text-xs text-slate-500">{left.map((c) => <li key={c.id}>{c.full_name} — <span className="text-amber-600">{excluded(c)}</span></li>)}</ul>}
            </div>
            <button type="button" data-testid="bulk-wa-start" disabled={!queue.length || !text.trim()} onClick={() => { setStatus({}); setPos(0); setStep("send"); }}
              className="w-full bg-[#25D366] hover:bg-[#1fb857] text-white py-2.5 rounded-xl font-semibold text-sm disabled:opacity-50">Start sending</button>
          </div>
        )}
        {step === "send" && cur && (
          <div className="space-y-3" data-testid="bulk-wa-step">
            <div className="flex items-center justify-between text-xs text-slate-500">
              <span data-testid="bulk-wa-progress">{pos + 1} of {queue.length} · {sent} sent, {skipped} skipped</span>
            </div>
            <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-[#25D366] transition-all" style={{ width: `${((pos + (status[cur.id] ? 1 : 0)) / queue.length) * 100}%` }} /></div>
            <div className="rounded-xl border border-slate-100 p-4">
              <div className="font-head font-bold text-slate-900">{cur.full_name}</div>
              <div className="text-xs text-slate-500 mb-2">{waDisplay(cur.phone)}{cur.file_number ? ` · File ${cur.file_number}` : ""}</div>
              <p className="text-sm text-slate-700 whitespace-pre-wrap bg-[#25D366]/5 rounded-lg p-3">{msgFor(cur)}</p>
            </div>
            <button type="button" data-testid="bulk-wa-open" onClick={openChat} className="w-full inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1fb857] text-white py-3 rounded-xl font-semibold">
              <WhatsAppIcon className="h-4 w-4" /> {status[cur.id] === "sent" ? "Open again" : "Open WhatsApp"}
            </button>
            <div className="flex gap-2">
              <button type="button" data-testid="bulk-wa-skip" onClick={() => next(true)} className="flex-1 inline-flex items-center justify-center gap-1.5 py-2.5 rounded-xl border border-slate-200 text-sm font-semibold text-slate-600"><SkipForward className="h-4 w-4" /> Skip</button>
              <button type="button" data-testid="bulk-wa-next" onClick={() => next(false)} className="flex-1 inline-flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-primary text-white text-sm font-semibold">Next <ArrowRight className="h-4 w-4" /></button>
            </div>
            <p className="text-[11px] text-slate-400">Press send in WhatsApp, then come back here and click Next.</p>
          </div>
        )}
        {step === "done" && (
          <div className="text-center space-y-3 py-4" data-testid="bulk-wa-done">
            <CheckCircle2 className="h-10 w-10 text-emerald-500 mx-auto" />
            <div className="font-head font-bold text-slate-900">All done</div>
            <div className="text-sm text-slate-500">{sent} opened · {skipped} skipped · {left.length} left out</div>
            <button type="button" data-testid="bulk-wa-finish" onClick={finish} className="bg-primary text-white px-5 py-2.5 rounded-xl text-sm font-semibold">Close</button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
